import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Trash2, RefreshCw, Lock, Search, ArrowLeft, Phone, Mail, Download } from "lucide-react";
import { LOGO } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "moums_admin_token";
const inputClass = "w-full rounded-2xl border border-beige bg-cream px-4 py-3 text-chocolate placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-strawberry/50 focus:border-strawberry transition";

const TYPE_LABEL = { devis: "Devis", contact: "Contact", rappel: "Rappel" };
const TYPE_COLOR = { devis: "bg-strawberry/10 text-strawberry", contact: "bg-sky/30 text-chocolate", rappel: "bg-sunny/20 text-[#a5670f]" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Connexion réussie 👋");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-hover border border-beige">
        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO} alt="Moum's" className="h-16 w-16 object-contain" />
          <div className="mt-3 flex items-center gap-2 text-strawberry"><Lock size={18} /><span className="font-semibold">Espace privé</span></div>
          <h1 className="mt-2 font-heading text-3xl font-semibold text-chocolate">Tableau de bord Moum's</h1>
        </div>
        <label className="block text-sm font-semibold text-chocolate mb-1.5">Email</label>
        <input data-testid="admin-email" type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" />
        <label className="block text-sm font-semibold text-chocolate mb-1.5 mt-4">Mot de passe</label>
        <input data-testid="admin-password" type="password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <button data-testid="admin-login-btn" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-strawberry px-6 py-3.5 font-semibold text-white hover:bg-[#B83044] transition disabled:opacity-60">
          {loading ? "Connexion…" : "Se connecter"}
        </button>
        <Link to="/" className="mt-4 flex items-center justify-center gap-2 text-sm text-cocoa hover:text-strawberry">
          <ArrowLeft size={16} /> Retour au site
        </Link>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showExport, setShowExport] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, auth);
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expirée"); onLogout(); }
      else toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, auth);
      setQuotes((prev) => prev.filter((x) => x.id !== id));
      toast.success("Demande supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const filtered = quotes.filter((x) => {
    const okType = filter === "all" || x.type === filter;
    const s = q.toLowerCase();
    const okSearch = !s || `${x.nom} ${x.prenom} ${x.email} ${x.telephone} ${x.type_evenement}`.toLowerCase().includes(s);
    return okType && okSearch;
  });

  const counts = {
    all: quotes.length,
    devis: quotes.filter((x) => x.type === "devis").length,
    contact: quotes.filter((x) => x.type === "contact").length,
    rappel: quotes.filter((x) => x.type === "rappel").length,
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  const EXPORT_COLS = [
    ["Date", (x) => fmt(x.created_at)],
    ["Type", (x) => x.type],
    ["Nom", (x) => x.nom],
    ["Prénom", (x) => x.prenom],
    ["Téléphone", (x) => x.telephone],
    ["Email", (x) => x.email],
    ["Date événement", (x) => x.date_evenement],
    ["Adresse", (x) => x.adresse],
    ["Nb personnes", (x) => x.nombre_personnes],
    ["Type événement", (x) => x.type_evenement],
    ["Saveurs", (x) => x.saveurs],
    ["Options", (x) => x.options],
    ["Topping/coulis suppl.", (x) => x.topping_sup],
    ["Souhaite être rappelé", (x) => (x.rappel_accepte ? "Oui" : "Non")],
    ["Message", (x) => x.message],
  ];

  const download = (content, filename, mime) => {
    const blob = new Blob(["\ufeff" + content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    setShowExport(false);
  };

  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const stamp = () => new Date().toISOString().slice(0, 10);

  const exportCSV = () => {
    const header = EXPORT_COLS.map((c) => esc(c[0])).join(";");
    const rows = filtered.map((x) => EXPORT_COLS.map((c) => esc(c[1](x))).join(";"));
    download([header, ...rows].join("\r\n"), `moums-demandes-${stamp()}.csv`, "text/csv;charset=utf-8;");
  };

  const exportExcel = () => {
    const th = EXPORT_COLS.map((c) => `<th>${c[0]}</th>`).join("");
    const trs = filtered.map((x) => `<tr>${EXPORT_COLS.map((c) => `<td>${String(c[1](x) ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`).join("")}</tr>`).join("");
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></body></html>`;
    download(html, `moums-demandes-${stamp()}.xls`, "application/vnd.ms-excel;charset=utf-8;");
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-white border-b border-beige sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Moum's" className="h-11 w-11 object-contain" />
            <div>
              <h1 className="font-heading text-2xl font-semibold text-chocolate leading-none">Demandes reçues</h1>
              <p className="text-xs text-cocoa">Tableau de bord privé</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button data-testid="admin-export-btn" onClick={() => setShowExport((v) => !v)} className="inline-flex items-center gap-2 rounded-full bg-white border border-beige px-4 py-2 text-sm font-semibold text-chocolate hover:bg-pastelbg transition">
                <Download size={16} /> Exporter
              </button>
              {showExport && (
                <div data-testid="admin-export-menu" className="absolute right-0 mt-2 w-44 rounded-2xl bg-white border border-beige shadow-hover overflow-hidden z-20">
                  <button data-testid="admin-export-csv" onClick={exportCSV} className="w-full text-left px-4 py-3 text-sm text-chocolate hover:bg-pastelbg transition">📄 Format CSV</button>
                  <button data-testid="admin-export-excel" onClick={exportExcel} className="w-full text-left px-4 py-3 text-sm text-chocolate hover:bg-pastelbg transition border-t border-beige">📊 Format Excel</button>
                </div>
              )}
            </div>
            <button data-testid="admin-refresh" onClick={load} className="h-10 w-10 rounded-full bg-cream border border-beige flex items-center justify-center text-cocoa hover:text-strawberry transition"><RefreshCw size={18} /></button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-strawberry px-4 py-2 text-sm font-semibold text-white hover:bg-[#B83044] transition"><LogOut size={16} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap gap-3 mb-5">
          {["all", "devis", "contact", "rappel"].map((t) => (
            <button
              key={t}
              data-testid={`admin-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`rounded-full px-5 py-2 text-sm font-semibold border transition ${filter === t ? "bg-chocolate text-cream border-chocolate" : "bg-white text-chocolate border-beige hover:bg-pastelbg"}`}
            >
              {t === "all" ? "Toutes" : TYPE_LABEL[t]} ({counts[t]})
            </button>
          ))}
          <div className="relative ml-auto min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full rounded-full border border-beige bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-strawberry/40" />
          </div>
        </div>

        {loading ? (
          <p className="text-cocoa py-16 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty" className="rounded-3xl bg-white border border-beige p-16 text-center text-cocoa">Aucune demande pour le moment.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-quotes-list">
            {filtered.map((x) => (
              <div key={x.id} data-testid={`admin-quote-${x.id}`} className="rounded-3xl bg-white border border-beige p-6 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${TYPE_COLOR[x.type] || "bg-beige"}`}>{TYPE_LABEL[x.type] || x.type}</span>
                    <h3 className="mt-2 font-heading text-2xl font-semibold text-chocolate">{x.prenom} {x.nom}</h3>
                    <p className="text-xs text-muted-foreground">{fmt(x.created_at)}</p>
                  </div>
                  <button data-testid={`admin-delete-${x.id}`} onClick={() => remove(x.id)} className="h-9 w-9 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-100 transition" aria-label="Supprimer"><Trash2 size={16} /></button>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <a href={`tel:${x.telephone}`} className="inline-flex items-center gap-1.5 text-cocoa hover:text-strawberry"><Phone size={14} /> {x.telephone}</a>
                  <a href={`mailto:${x.email}`} className="inline-flex items-center gap-1.5 text-cocoa hover:text-strawberry"><Mail size={14} /> {x.email}</a>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-1 text-sm text-cocoa">
                  {x.type_evenement && <p><span className="font-semibold text-chocolate">Événement :</span> {x.type_evenement}</p>}
                  {x.date_evenement && <p><span className="font-semibold text-chocolate">Date :</span> {x.date_evenement}</p>}
                  {x.adresse && <p><span className="font-semibold text-chocolate">Adresse :</span> {x.adresse}</p>}
                  {x.nombre_personnes && <p><span className="font-semibold text-chocolate">Personnes :</span> {x.nombre_personnes}</p>}
                  {x.saveurs && <p><span className="font-semibold text-chocolate">Saveurs :</span> {x.saveurs}</p>}
                  {x.options && <p><span className="font-semibold text-chocolate">Options :</span> {x.options}</p>}
                  {x.topping_sup && <p><span className="font-semibold text-chocolate">Topping/coulis suppl. :</span> {x.topping_sup}</p>}
                  {x.message && <p><span className="font-semibold text-chocolate">Message :</span> {x.message}</p>}
                  <p><span className="font-semibold text-chocolate">Souhaite être rappelé :</span> {x.rappel_accepte ? "Oui ✅" : "Non"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  useEffect(() => { document.title = "Admin — Moum's"; }, []);

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
